import { Fragment } from 'react'
import { createPortal } from 'react-dom'
import { ViewTitle } from '../../reusable'
import { Spinner } from '../../ui/spinner'
import { FundingClientDetailsView } from '../fundingClientDetailsView'
import { DualRangeSlider } from '../../ui/ineligibleFundingClients/dualRangeSlider/DualRangeSlider'
import { IneligibleFundingClientsMetricCard } from '../../ui/ineligibleFundingClients/metricCard/IneligibleFundingClientsMetricCard'
import styles from './styles.module.scss'
import { INELIGIBLE_PAGE_SIZE, useIneligibleFundingClientsViewState } from './useIneligibleFundingClientsViewState'

const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A'
    try {
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    } catch {
        return dateString
    }
}

const formatCurrency = (value: number): string =>
    `£${(value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const IneligibleFundingClientsView = () => {
    const {
        loading,
        error,
        removedClients,
        search,
        setSearch,
        expandedClients,
        detailsModalClientId,
        setDetailsModalClientId,
        scopedRawClaims,
        explorerBounds,
        explorerDefendantCounts,
        explorerIntroducerCounts,
        explorerBatchCounts,
        explorerBatchClientCounts,
        explorerLoanValueCounts,
        explorerLoanValues,
        explorerLoanValuesSelected,
        setExplorerLoanValuesSelected,
        explorerQuantumMin,
        setExplorerQuantumMin,
        explorerQuantumMax,
        setExplorerQuantumMax,
        pendingQuantumMin,
        setPendingQuantumMin,
        pendingQuantumMax,
        setPendingQuantumMax,
        quantumApplying,
        setQuantumApplying,
        explorerDefendants,
        setExplorerDefendants,
        explorerIntroducers,
        setExplorerIntroducers,
        explorerBatches,
        setExplorerBatches,
        explorerStatuses,
        setExplorerStatuses,
        explorerCfa,
        setExplorerCfa,
        defendantSort,
        setDefendantSort,
        showExplorerClaimsModal,
        setShowExplorerClaimsModal,
        openExplorerFilter,
        setOpenExplorerFilter,
        filterPopoverPosition,
        setFilterPopoverPosition,
        explorerFilterBarRef,
        sortedExplorerDefendants,
        sortedExplorerIntroducers,
        sortedExplorerBatches,
        explorerFilteredNoQuantum,
        tableClients,
        paginatedClients,
        currentPage,
        totalPages,
        handlePageChange,
        toggleExplorerSet,
        resetExplorer,
        toggleExplorerFilter,
        hasSearch,
        searchSummary,
        hasLoanFilter,
        hasQuantumFilter,
        hasCfaFilter,
        hasDefendantFilter,
        hasIntroducerFilter,
        hasBatchFilter,
        hasStatusFilter,
        loanFilterSummary,
        quantumFilterSummary,
        cfaFilterSummary,
        defendantFilterSummary,
        introducerFilterSummary,
        batchFilterSummary,
        statusFilterSummary,
        resetExplorerFilter,
        explorerSelectionRemoved,
        exportExplorerResultsCsv,
        explorerSelectedClaimValue,
        explorerClaimValuePct,
        explorerSelectedClients,
        explorerClientPct,
        toggleClient,
    } = useIneligibleFundingClientsViewState()

    const exportDisabled = loading || !!error || removedClients.length === 0 || explorerSelectionRemoved.length === 0

    return (
        <div className={styles.container}>
            <ViewTitle
                text={'Ineligible Funding Clients'}
                button={
                    <button
                        type="button"
                        className={styles.exportResultsBtn}
                        disabled={exportDisabled}
                        onClick={() => exportExplorerResultsCsv()}
                    >
                        Export results
                    </button>
                }
            />
            {detailsModalClientId != null &&
                createPortal(
                    <div
                        className={styles.claimsModalOverlay}
                        role="presentation"
                        onClick={(e) => e.target === e.currentTarget && setDetailsModalClientId(null)}
                    >
                        <div
                            className={styles.claimsModal}
                            role="dialog"
                            aria-modal
                            aria-labelledby="client-details-modal-title"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={styles.claimsModalHeader}>
                                <div>
                                    <h3 id="client-details-modal-title">Funding client</h3>
                                    <span>Client ID {detailsModalClientId}</span>
                                </div>
                                <button
                                    type="button"
                                    className={styles.claimsModalClose}
                                    onClick={() => setDetailsModalClientId(null)}
                                    aria-label="Close client details"
                                >
                                    <span aria-hidden="true"></span>
                                </button>
                            </div>
                            <div className={styles.clientDetailsModalBody}>
                                <FundingClientDetailsView
                                    clientIdOverride={detailsModalClientId}
                                    embedded
                                    onClose={() => setDetailsModalClientId(null)}
                                />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            {loading && <Spinner />}

            {error && !loading && (
                <div className={styles.errorState}>
                    <p>{error}</p>
                </div>
            )}

            {!loading && !error && (
                <>
                    {showExplorerClaimsModal && (
                        <div className={styles.claimsModalOverlay} onClick={() => setShowExplorerClaimsModal(false)}>
                            <div className={styles.claimsModal} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.claimsModalHeader}>
                                    <div>
                                        <h3>Rejected claims</h3>
                                        <span>{explorerSelectionRemoved.length.toLocaleString()} claims match the current filters</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.claimsModalClose}
                                        onClick={() => setShowExplorerClaimsModal(false)}
                                        aria-label="Close claims modal"
                                    >
                                        <span aria-hidden="true"></span>
                                    </button>
                                </div>
                                <div className={styles.claimsModalTableWrap}>
                                    <table className={styles.claimsModalTable}>
                                        <thead>
                                            <tr>
	                                                <th>Funder Case Ref</th>
	                                                <th>Introducer Case Ref</th>
	                                                <th>Batch</th>
	                                                <th>Introducer</th>
	                                                <th>Lender</th>
                                                <th>Loan Value</th>
                                                <th>Quantum</th>
                                                <th>Status</th>
                                                <th>CFA Date</th>
                                                <th>Removed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {explorerSelectionRemoved.map((claim, index) => (
                                                <tr key={`${claim.funder_case_ref || claim.introducer_case_ref || 'claim'}-${index}`}>
	                                                    <td>{claim.funder_case_ref || '-'}</td>
	                                                    <td>{claim.introducer_case_ref || claim.law_firm_secondary_reference || '-'}</td>
	                                                    <td>{claim.batch_id || '-'}</td>
	                                                    <td>{claim.introducer || '-'}</td>
	                                                    <td>{claim.defendant || '-'}</td>
                                                    <td>{formatCurrency(claim.loan_value)}</td>
                                                    <td>{formatCurrency(claim.quantum_individual)}</td>
                                                    <td>{claim.client_status || '-'}</td>
                                                    <td>{formatDate(claim.cfa_date)}</td>
                                                    <td>{formatDate(claim.audit_removed_date || '')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

		                    {scopedRawClaims.removed.length > 0 && (
		                        <>
		                        <div className={styles.explorerToolbar}>
				                                    <div ref={explorerFilterBarRef} className={styles.filterMenuBar} aria-label="Explorer filters">
                                            <div className={styles.filterMenuItem} style={{ order: 0 }}>
                                                <button
                                                    type="button"
                                                    className={`${styles.filterMenuPill} ${hasSearch ? styles.filterMenuPillActive : ''}`}
                                                    aria-expanded={openExplorerFilter === 'search'}
                                                    onClick={(e) => toggleExplorerFilter('search', e, 360)}
                                                >
                                                    {hasSearch && (
                                                        <span
                                                            className={styles.filterMenuReset}
                                                            aria-label="Clear search"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                resetExplorerFilter('search')
                                                            }}
                                                        />
                                                    )}
                                                    <span className={styles.filterMenuName}>Search</span>
                                                    {hasSearch && <span className={styles.filterMenuValue}>{searchSummary}</span>}
                                                    <span className={hasSearch ? styles.filterMenuEdit : styles.filterMenuPlus} aria-hidden="true" />
                                                </button>
                                                {openExplorerFilter === 'search' && filterPopoverPosition && (
                                                    <div className={`${styles.filterPopover} ${styles.filterPopoverWide}`} style={filterPopoverPosition}>
                                                        <span className={styles.filterSectionLabel}>Search clients</span>
                                                        <input
                                                            autoFocus
                                                            className={styles.searchInput}
                                                            type="text"
                                                            placeholder="Name, email, case ref, batch…"
                                                            value={search}
                                                            onChange={(e) => setSearch(e.target.value)}
                                                        />
                                                    </div>
                                                )}
                                            </div>
		                                        <div className={styles.filterMenuItem} style={{ order: 5 }}>
			                                            <button
			                                                type="button"
			                                                className={`${styles.filterMenuPill} ${hasLoanFilter ? styles.filterMenuPillActive : ''}`}
			                                                aria-expanded={openExplorerFilter === 'loan'}
					                                                onClick={(e) => toggleExplorerFilter('loan', e)}
				                                            >
				                                                {hasLoanFilter && (
				                                                    <span
				                                                        className={styles.filterMenuReset}
				                                                        aria-label="Reset loan value filter"
				                                                        onClick={(e) => {
				                                                            e.stopPropagation()
				                                                            resetExplorerFilter('loan')
				                                                        }}
				                                                    />
					                                                )}
					                                                <span className={styles.filterMenuName}>Loan value</span>
					                                                {hasLoanFilter && <span className={styles.filterMenuValue}>{loanFilterSummary}</span>}
					                                                <span className={hasLoanFilter ? styles.filterMenuEdit : styles.filterMenuPlus} aria-hidden="true" />
					                                            </button>
			                                            {openExplorerFilter === 'loan' && (
			                                                <div className={`${styles.filterPopover} ${styles.filterPopoverWide}`} style={filterPopoverPosition || undefined}>
													<span className={styles.filterSectionLabel}>Loan value (£)</span>
													<div className={styles.cardWrap} role="group" aria-label="Filter by loan value">
															{explorerLoanValues.map((loanValue) => {
																const active = explorerLoanValuesSelected.has(loanValue)
																const count = explorerLoanValueCounts[loanValue] || 0
																return (
																<button
																	key={loanValue}
																	type="button"
																	aria-pressed={active}
																	className={`${styles.filterCard} ${active ? styles.filterCardActive : ''}`}
																	onClick={() => {
																		const newSet = new Set(explorerLoanValuesSelected)
																		if (newSet.has(loanValue)) newSet.delete(loanValue)
																		else newSet.add(loanValue)
																		setExplorerLoanValuesSelected(newSet)
																	}}
																>
																	<span className={styles.filterCardLabel}>{formatCurrency(loanValue)}</span>
																	<span className={styles.filterCardCount}>
																		{count.toLocaleString()} {count === 1 ? 'claim' : 'claims'}
																	</span>
																</button>
															)
														})}
													</div>
			                                                </div>
			                                            )}
			                                        </div>
			                                        <div className={styles.filterMenuItem} style={{ order: 4 }}>
			                                            <button
			                                                type="button"
			                                                className={`${styles.filterMenuPill} ${hasQuantumFilter ? styles.filterMenuPillActive : ''}`}
			                                                aria-expanded={openExplorerFilter === 'quantum'}
					                                                onClick={(e) => toggleExplorerFilter('quantum', e)}
				                                            >
				                                                {hasQuantumFilter && (
				                                                    <span
				                                                        className={styles.filterMenuReset}
				                                                        aria-label="Reset quantum filter"
				                                                        onClick={(e) => {
				                                                            e.stopPropagation()
				                                                            resetExplorerFilter('quantum')
				                                                        }}
				                                                    />
					                                                )}
					                                                <span className={styles.filterMenuName}>Quantum</span>
					                                                {hasQuantumFilter && <span className={styles.filterMenuValue}>{quantumFilterSummary}</span>}
					                                                <span className={hasQuantumFilter ? styles.filterMenuEdit : styles.filterMenuPlus} aria-hidden="true" />
					                                            </button>
			                                            {openExplorerFilter === 'quantum' && (
			                                                <div className={`${styles.filterPopover} ${styles.filterPopoverWide}`} style={filterPopoverPosition || undefined}>
			                                                    <div className={styles.filterSectionLabel}>
			                                                        <span>Quantum (£)</span>
			                                                        <button
			                                                            type="button"
			                                                            className={styles.filterUpdateBtn}
			                                                            disabled={(() => {
			                                                                const currentMax = explorerQuantumMax === Infinity ? explorerBounds.quantumMax : explorerQuantumMax
			                                                                const pendingMaxResolved = pendingQuantumMax === Infinity ? explorerBounds.quantumMax : pendingQuantumMax
			                                                                const dirty = pendingQuantumMin !== explorerQuantumMin || pendingMaxResolved !== currentMax
			                                                                return quantumApplying || !dirty
			                                                            })()}
			                                                            onClick={() => {
			                                                                setQuantumApplying(true)
			                                                                setExplorerQuantumMin(pendingQuantumMin)
			                                                                setExplorerQuantumMax(pendingQuantumMax)
			                                                                setOpenExplorerFilter(null)
			                                                                setFilterPopoverPosition(null)
			                                                                // keep the "applying" state briefly so the user sees feedback
			                                                                window.setTimeout(() => setQuantumApplying(false), 250)
			                                                            }}
			                                                        >
			                                                            {quantumApplying && <span className={styles.miniSpinner} aria-hidden="true" />}
			                                                            Update
			                                                        </button>
			                                                    </div>
			                                                    <div className={styles.quantumLegend} aria-label="Quantum histogram key">
			                                                        <span className={styles.quantumLegendItem}>
			                                                            <span className={`${styles.quantumLegendDot} ${styles.quantumLegendDotOverall}`} aria-hidden="true" />
			                                                            Overall rejected claims
			                                                        </span>
			                                                        <span className={styles.quantumLegendItem}>
			                                                            <span className={`${styles.quantumLegendDot} ${styles.quantumLegendDotFiltered}`} aria-hidden="true" />
			                                                            After other filters
			                                                        </span>
			                                                        <span className={styles.quantumLegendItem}>
			                                                            <span className={`${styles.quantumLegendDot} ${styles.quantumLegendDotSelected}`} aria-hidden="true" />
			                                                            In selected range
			                                                        </span>
			                                                    </div>
			                                                    <DualRangeSlider
			                                                        min={0}
			                                                        max={explorerBounds.quantumMax}
			                                                        valueMin={pendingQuantumMin}
			                                                        valueMax={pendingQuantumMax === Infinity ? explorerBounds.quantumMax : pendingQuantumMax}
			                                                        step={Math.max(1, Math.round(explorerBounds.quantumMax / 200))}
			                                                        histogramValues={scopedRawClaims.removed.map((c) => c.quantum_individual || 0)}
			                                                        histogramOverlayValues={explorerFilteredNoQuantum.map((c) => c.quantum_individual || 0)}
			                                                        onChangeMin={setPendingQuantumMin}
			                                                        onChangeMax={(v) => setPendingQuantumMax(v >= explorerBounds.quantumMax ? Infinity : v)}
			                                                        formatValue={formatCurrency}
			                                                    />
			                                                </div>
			                                            )}
			                                        </div>
			                                        <div className={styles.filterMenuItem} style={{ order: 6 }}>
			                                            <button
			                                                type="button"
			                                                className={`${styles.filterMenuPill} ${hasCfaFilter ? styles.filterMenuPillActive : ''}`}
			                                                aria-expanded={openExplorerFilter === 'cfa'}
					                                                onClick={(e) => toggleExplorerFilter('cfa', e, 320)}
				                                            >
				                                                {hasCfaFilter && (
				                                                    <span
				                                                        className={styles.filterMenuReset}
				                                                        aria-label="Reset CFA date filter"
				                                                        onClick={(e) => {
				                                                            e.stopPropagation()
				                                                            resetExplorerFilter('cfa')
				                                                        }}
				                                                    />
					                                                )}
					                                                <span className={styles.filterMenuName}>CFA date</span>
					                                                {hasCfaFilter && <span className={styles.filterMenuValue}>{cfaFilterSummary}</span>}
					                                                <span className={hasCfaFilter ? styles.filterMenuEdit : styles.filterMenuPlus} aria-hidden="true" />
					                                            </button>
			                                            {openExplorerFilter === 'cfa' && (
			                                                <div className={styles.filterPopover} style={filterPopoverPosition || undefined}>
			                                                    <span className={styles.filterSectionLabel}>CFA date</span>
			                                                    <div className={styles.radioGroup}>
			                                                        {(['all', 'present', 'missing'] as const).map((opt) => (
			                                                            <label key={opt} className={styles.radioLabel}>
			                                                                <input
			                                                                    type="radio"
			                                                                    name="cfa"
			                                                                    checked={explorerCfa === opt}
			                                                                    onChange={() => setExplorerCfa(opt)}
			                                                                />
			                                                                {opt === 'all' ? 'All' : opt === 'present' ? 'Has CFA date' : 'Missing CFA date'}
			                                                            </label>
			                                                        ))}
			                                                    </div>
			                                                </div>
			                                            )}
			                                        </div>
			                                        <div className={styles.filterMenuItem} style={{ order: 3 }}>
			                                            <button
			                                                type="button"
			                                                className={`${styles.filterMenuPill} ${hasDefendantFilter ? styles.filterMenuPillActive : ''}`}
			                                                aria-expanded={openExplorerFilter === 'defendant'}
					                                                onClick={(e) => toggleExplorerFilter('defendant', e)}
				                                            >
				                                                {hasDefendantFilter && (
				                                                    <span
				                                                        className={styles.filterMenuReset}
				                                                        aria-label="Reset lender filter"
				                                                        onClick={(e) => {
				                                                            e.stopPropagation()
				                                                            resetExplorerFilter('defendant')
				                                                        }}
				                                                    />
					                                                )}
					                                                <span className={styles.filterMenuName}>Lender</span>
					                                                {hasDefendantFilter && <span className={styles.filterMenuValue}>{defendantFilterSummary}</span>}
					                                                <span className={hasDefendantFilter ? styles.filterMenuEdit : styles.filterMenuPlus} aria-hidden="true" />
					                                            </button>
			                                            {openExplorerFilter === 'defendant' && (
			                                                <div className={`${styles.filterPopover} ${styles.filterPopoverWide}`} style={filterPopoverPosition || undefined}>
			                                                    <span className={styles.filterSectionLabel}>
			                                                        Lender
			                                                        <span className={styles.filterSectionActions}>
				                                                            <select
				                                                                className={styles.sortSelect}
				                                                                value={defendantSort}
				                                                                aria-label="Sort lenders"
			                                                                onChange={(e) => setDefendantSort(e.target.value as 'name' | 'claims')}
			                                                            >
			                                                                <option value="claims">Sort by claims</option>
			                                                                <option value="name">Sort by name</option>
			                                                            </select>
			                                                            {explorerDefendants.length > 0 && (
			                                                                <button className={styles.clearLink} onClick={() => setExplorerDefendants([])}>
			                                                                    clear
			                                                                </button>
			                                                            )}
			                                                        </span>
			                                                    </span>
			                                                    <div className={styles.cardWrap} role="group" aria-label="Filter by lender">
			                                                        {sortedExplorerDefendants.map((d) => {
			                                                            const active = explorerDefendants.includes(d)
			                                                            const count = explorerDefendantCounts[d] || 0
			                                                            return (
			                                                                <button
			                                                                    key={d}
			                                                                    type="button"
			                                                                    title={d}
			                                                                    aria-pressed={active}
			                                                                    className={`${styles.filterCard} ${active ? styles.filterCardActive : ''}`}
			                                                                    onClick={() => toggleExplorerSet(d, explorerDefendants, setExplorerDefendants)}
			                                                                >
			                                                                    <span className={styles.filterCardLabel}>{d}</span>
			                                                                    <span className={styles.filterCardCount}>
			                                                                        {count.toLocaleString()} {count === 1 ? 'claim' : 'claims'}
			                                                                    </span>
			                                                                </button>
			                                                            )
			                                                        })}
			                                                    </div>
			                                                </div>
			                                            )}
			                                        </div>
			                                        <div className={styles.filterMenuItem} style={{ order: 2 }}>
			                                            <button
			                                                type="button"
			                                                className={`${styles.filterMenuPill} ${hasIntroducerFilter ? styles.filterMenuPillActive : ''}`}
			                                                aria-expanded={openExplorerFilter === 'introducer'}
				            				                        onClick={(e) => toggleExplorerFilter('introducer', e)}
				                                            >
				                                                {hasIntroducerFilter && (
				                                                    <span
				                                                        className={styles.filterMenuReset}
				                                                        aria-label="Reset introducer filter"
				                                                        onClick={(e) => {
				                                                            e.stopPropagation()
				                                                            resetExplorerFilter('introducer')
				                                                        }}
				                                                    />
					                                                )}
					                                                <span className={styles.filterMenuName}>Introducer</span>
					                                                {hasIntroducerFilter && <span className={styles.filterMenuValue}>{introducerFilterSummary}</span>}
					                                                <span className={hasIntroducerFilter ? styles.filterMenuEdit : styles.filterMenuPlus} aria-hidden="true" />
					                                            </button>
			                                            {openExplorerFilter === 'introducer' && (
			                                                <div className={`${styles.filterPopover} ${styles.filterPopoverWide}`} style={filterPopoverPosition || undefined}>
			                                                    <span className={styles.filterSectionLabel}>
			                                                        Introducer
			                                                        {explorerIntroducers.length > 0 && (
			                                                            <button className={styles.clearLink} onClick={() => setExplorerIntroducers([])}>
			                                                                clear
			                                                            </button>
			                                                        )}
			                                                    </span>
			                                                    <div className={styles.cardWrap} role="group" aria-label="Filter by introducer">
			                                                        {sortedExplorerIntroducers.map((i) => {
			                                                            const active = explorerIntroducers.includes(i)
			                                                            const count = explorerIntroducerCounts[i] || 0
			                                                            return (
			                                                                <button
			                                                                    key={i}
			                                                                    type="button"
			                                                                    title={i}
			                                                                    aria-pressed={active}
			                                                                    className={`${styles.filterCard} ${active ? styles.filterCardActive : ''}`}
			                                                                    onClick={() => toggleExplorerSet(i, explorerIntroducers, setExplorerIntroducers)}
			                                                                >
			                                                                    <span className={styles.filterCardLabel}>{i}</span>
			                                                                    <span className={styles.filterCardCount}>
			                                                                        {count.toLocaleString()} {count === 1 ? 'claim' : 'claims'}
			                                                                    </span>
			                                                                </button>
			                                                            )
			                                                        })}
			                                                    </div>
			                                                </div>
			                                            )}
			                                        </div>
			                                        <div className={styles.filterMenuItem} style={{ order: 1 }}>
			                                            <button
			                                                type="button"
			                                                className={`${styles.filterMenuPill} ${hasBatchFilter ? styles.filterMenuPillActive : ''}`}
			                                                aria-expanded={openExplorerFilter === 'batch'}
					                                                onClick={(e) => toggleExplorerFilter('batch', e)}
				                                            >
				                                                {hasBatchFilter && (
				                                                    <span
				                                                        className={styles.filterMenuReset}
				                                                        aria-label="Reset batch filter"
				                                                        onClick={(e) => {
				                                                            e.stopPropagation()
				                                                            resetExplorerFilter('batch')
				                                                        }}
				                                                    />
					                                                )}
					                                                <span className={styles.filterMenuName}>Batch</span>
					                                                {hasBatchFilter && <span className={styles.filterMenuValue}>{batchFilterSummary}</span>}
					                                                <span className={hasBatchFilter ? styles.filterMenuEdit : styles.filterMenuPlus} aria-hidden="true" />
					                                            </button>
			                                            {openExplorerFilter === 'batch' && (
			                                                <div className={`${styles.filterPopover} ${styles.filterPopoverWide}`} style={filterPopoverPosition || undefined}>
			                                                    <span className={styles.filterSectionLabel}>
			                                                        Batch
			                                                        {explorerBatches.length > 0 && (
			                                                            <button className={styles.clearLink} onClick={() => setExplorerBatches([])}>
			                                                                clear
			                                                            </button>
			                                                        )}
			                                                    </span>
			                                                    <div className={styles.cardWrap} role="group" aria-label="Filter by batch">
				                                                        {sortedExplorerBatches.map((b) => {
				                                                            const active = explorerBatches.includes(b)
				                                                            const count = explorerBatchCounts[b] || 0
				                                                            const clientCount = explorerBatchClientCounts[b]?.size || 0
				                                                            return (
			                                                                <button
			                                                                    key={b}
			                                                                    type="button"
			                                                                    title={b}
			                                                                    aria-pressed={active}
			                                                                    className={`${styles.filterCard} ${active ? styles.filterCardActive : ''}`}
			                                                                    onClick={() => toggleExplorerSet(b, explorerBatches, setExplorerBatches)}
			                                                                >
			                                                                    <span className={styles.filterCardLabel}>{b}</span>
				                                                                    <span className={styles.filterCardCount}>
				                                                                        {clientCount.toLocaleString()} {clientCount === 1 ? 'client' : 'clients'} / {count.toLocaleString()} {count === 1 ? 'claim' : 'claims'}
				                                                                    </span>
			                                                                </button>
			                                                            )
			                                                        })}
			                                                    </div>
			                                                </div>
			                                            )}
			                                        </div>
			                                        <div className={styles.filterMenuItem} style={{ order: 7 }}>
			                                            <button
			                                                type="button"
			                                                className={`${styles.filterMenuPill} ${hasStatusFilter ? styles.filterMenuPillActive : ''}`}
			                                                aria-expanded={openExplorerFilter === 'status'}
					                                                onClick={(e) => toggleExplorerFilter('status', e)}
				                                            >
				                                                {hasStatusFilter && (
				                                                    <span
				                                                        className={styles.filterMenuReset}
				                                                        aria-label="Reset client status filter"
				                                                        onClick={(e) => {
				                                                            e.stopPropagation()
				                                                            resetExplorerFilter('status')
				                                                        }}
				                                                    />
					                                                )}
					                                                <span className={styles.filterMenuName}>Client status</span>
					                                                {hasStatusFilter && <span className={styles.filterMenuValue}>{statusFilterSummary}</span>}
					                                                <span className={hasStatusFilter ? styles.filterMenuEdit : styles.filterMenuPlus} aria-hidden="true" />
					                                            </button>
			                                            {openExplorerFilter === 'status' && (
			                                                <div className={`${styles.filterPopover} ${styles.filterPopoverWide}`} style={filterPopoverPosition || undefined}>
			                                                    <span className={styles.filterSectionLabel}>
			                                                        Client status
			                                                        {explorerStatuses.length > 0 && (
			                                                            <button className={styles.clearLink} onClick={() => setExplorerStatuses([])}>
			                                                                clear
			                                                            </button>
			                                                        )}
			                                                    </span>
			                                                    <div className={styles.pillWrap} role="group" aria-label="Filter by client status">
			                                                        {explorerBounds.statuses.map((s) => {
			                                                            const active = explorerStatuses.includes(s)
			                                                            return (
			                                                                <button
			                                                                    key={s}
			                                                                    type="button"
			                                                                    title={s}
			                                                                    aria-pressed={active}
			                                                                    className={`${styles.pill} ${active ? styles.pillActive : ''}`}
			                                                                    onClick={() => toggleExplorerSet(s, explorerStatuses, setExplorerStatuses)}
			                                                                >
			                                                                    <span className={styles.pillLabel}>{s}</span>
			                                                                </button>
			                                                            )
			                                                        })}
			                                                    </div>
			                                                </div>
				                                            )}
				                                        </div>
				                                    </div>
			                                    <button className={styles.resetBtn} onClick={resetExplorer}>Reset filters</button>
			                                </div>
										<div className={styles.metricCardsGrid}>
											<IneligibleFundingClientsMetricCard
												label="Ineligible Clients"
												value={explorerSelectedClients.toLocaleString()}
												pct={explorerClientPct}
												variant="orange"
												infoTooltipExtendRight
												info="How many different clients still show up after you’ve applied your filters—only clients with at least one claim removed in audit. The percentage is what share of all ineligible clients in this batch view that represents."
											/>
											<IneligibleFundingClientsMetricCard
												label="Ineligible Claims Value"
												value={formatCurrency(explorerSelectedClaimValue)}
												pct={explorerClaimValuePct}
												variant="blue"
												info="Adds up the quantum for every rejected claim that still matches your filters. The percentage is how much of all the quantum in this view comes from those claims."
											/>
											<IneligibleFundingClientsMetricCard
												label="Ineligible Claim Quantity"
												value={explorerSelectionRemoved.length.toLocaleString()}
												pct={
													scopedRawClaims.removed.length > 0
														? (explorerSelectionRemoved.length / scopedRawClaims.removed.length) * 100
														: 0
												}
												variant="purple"
												info="How many individual rejected claims match your filters. The bar fills by the share of all rejected claims in this view that those claims make up."
											/>
										</div>
		                        </>
			                    )}

	                    {removedClients.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No clients have been removed from any batch yet.</p>
                        </div>
	                    ) : tableClients.length === 0 ? (
	                        <div className={styles.emptyState}>
	                            <p>No clients match the current filters.</p>
	                        </div>
	                    ) : (
	                        <>
	                            <p className={styles.tableNote}>
	                                Showing{' '}
                                    {(Math.min((currentPage - 1) * INELIGIBLE_PAGE_SIZE + 1, tableClients.length)).toLocaleString()}
                                    {' '}–{' '}
                                    {Math.min(currentPage * INELIGIBLE_PAGE_SIZE, tableClients.length).toLocaleString()}
                                    {' '}of {tableClients.length.toLocaleString()} ineligible funding clients
                                    {tableClients.length < removedClients.length && ` (filtered from ${removedClients.length.toLocaleString()})`}
	                            </p>
	                            <div className={styles.tableWrapper}>
	                                <table className={styles.removedTable}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Client</th>
                                        <th>Email</th>
                                        <th>Batch</th>
                                        <th>Introducer</th>
                                        <th>Claims</th>
                                        <th>Value</th>
                                        <th>Removed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedClients.map((client) => {
                                        const key = `${client.batch_id}::${client.funder_client_id}`
                                        const expanded = expandedClients.has(key)
                                        const lastRemovedAt = client.claims
                                            .map((c) => c.audit_removed_date)
                                            .filter(Boolean)
                                            .sort()
                                            .pop()
                                        return (
                                            <Fragment key={key}>
                                                <tr
                                                    className={styles.clientRow}
                                                    onClick={() => toggleClient(key)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault()
                                                            toggleClient(key)
                                                        }
                                                    }}
                                                >
                                                    <td className={styles.expandCell}>{expanded ? '▾' : '▸'}</td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className={styles.clientNameLink}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setDetailsModalClientId(client.funder_client_id)
                                                            }}
                                                        >
                                                            {[client.title, client.first_name, client.last_name]
                                                                .filter(Boolean)
                                                                .join(' ') || 'Unknown'}
                                                        </button>
                                                    </td>
                                                    <td>{client.email_address || '-'}</td>
                                                    <td>{client.batch_id}</td>
                                                    <td>{client.introducer}</td>
                                                    <td>{client.claimsCount}</td>
                                                    <td>{formatCurrency(client.totalValue)}</td>
                                                    <td>{formatDate(lastRemovedAt)}</td>
                                                </tr>
                                                {expanded && (
                                                    <tr className={styles.expandedRow}>
                                                        <td colSpan={8}>
                                                            <table className={styles.claimsTable}>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Funder Case Ref</th>
                                                                        <th>Introducer Case Ref</th>
                                                                        <th>Lender</th>
                                                                        <th>Loan Value</th>
                                                                        <th>Quantum</th>
                                                                        <th>Status</th>
                                                                        <th>CFA Date</th>
                                                                        <th>Removed</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {client.claims.map((claim) => (
                                                                        <tr key={claim.funder_case_ref}>
                                                                            <td>{claim.funder_case_ref || '-'}</td>
                                                                            <td>
                                                                                {claim.introducer_case_ref ||
                                                                                    claim.law_firm_secondary_reference ||
                                                                                    '-'}
                                                                            </td>
                                                                            <td>{claim.defendant || '-'}</td>
                                                                            <td>{formatCurrency(claim.loan_value)}</td>
                                                                            <td>
                                                                                {formatCurrency(claim.quantum_individual)}
                                                                            </td>
                                                                            <td>{claim.client_status || '-'}</td>
                                                                            <td>{formatDate(claim.cfa_date)}</td>
                                                                            <td>{formatDate(claim.audit_removed_date)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        )
                                    })}
                                </tbody>
	                                </table>
	                            </div>

                            {totalPages > 1 && (
                                <div className={styles.paginationSection}>
                                    <div className={styles.paginationInfo}>
                                        <span>Page {currentPage} of {totalPages}</span>
                                        <span>
                                            Showing{' '}
                                            {((currentPage - 1) * INELIGIBLE_PAGE_SIZE + 1).toLocaleString()}
                                            {' '}–{' '}
                                            {Math.min(currentPage * INELIGIBLE_PAGE_SIZE, tableClients.length).toLocaleString()}
                                            {' '}of {tableClients.length.toLocaleString()} clients
                                        </span>
                                    </div>
                                    <div className={styles.paginationControls}>
                                        <button
                                            type="button"
                                            className={styles.paginationButton}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .filter((page) => page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2))
                                            .map((page, idx, arr) => (
                                                <div key={page} className={styles.paginationPages}>
                                                    {idx > 0 && arr[idx - 1] !== page - 1 && (
                                                        <span className={styles.paginationEllipsis}>...</span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className={`${styles.paginationButton} ${page === currentPage ? styles.active : ''}`}
                                                        onClick={() => handlePageChange(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                </div>
                                            ))}
                                        <button
                                            type="button"
                                            className={styles.paginationButton}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
	                        </>
	                    )}
                </>
            )}
        </div>
    )
}
